import { breakpoints } from '@components/composable';
import { propsFactory } from '@components/utils';

import type {
  TColumnBreakpoint,
  TColumnSpan,
  TColumnFlexBreakpoint,
  TColumnGridBreakpoint,
  TColumnSpanType
} from '../types';
import type { PropType } from 'vue';

export interface IColumnProps {
  tag: string | undefined;
  xs: TColumnBreakpoint<TColumnGridBreakpoint | TColumnFlexBreakpoint> | undefined;
  sm: TColumnBreakpoint<TColumnGridBreakpoint | TColumnFlexBreakpoint> | undefined;
  md: TColumnBreakpoint<TColumnGridBreakpoint | TColumnFlexBreakpoint> | undefined;
  lg: TColumnBreakpoint<TColumnGridBreakpoint | TColumnFlexBreakpoint> | undefined;
  xl: TColumnBreakpoint<TColumnGridBreakpoint | TColumnFlexBreakpoint> | undefined;
  span: TColumnSpan<TColumnSpanType>;
}
export interface IUseColumn {
  breakpoints: TColumnBreakpoint<TColumnGridBreakpoint | TColumnFlexBreakpoint>[];
}

const breakpointNames = breakpoints;

export const makeColumnProps = propsFactory(
  {
    /**
     * >320px
     */
    xs: {
      type: [Object, Number, String, Boolean] as PropType<
        TColumnBreakpoint<TColumnGridBreakpoint | TColumnFlexBreakpoint> | number | string | boolean
      >,
      default: undefined
    },
    /**
     * >672px
     */
    sm: {
      type: [Object, Number, String, Boolean] as PropType<
        TColumnBreakpoint<TColumnGridBreakpoint | TColumnFlexBreakpoint> | number | string | boolean
      >,
      default: undefined
    },
    /**
     * >1056px
     */
    md: {
      type: [Object, Number, String, Boolean] as PropType<
        TColumnBreakpoint<TColumnGridBreakpoint | TColumnFlexBreakpoint> | number | string | boolean
      >,
      default: undefined
    },
    /**
     * >1312px
     */
    lg: {
      type: [Object, Number, String, Boolean] as PropType<
        TColumnBreakpoint<TColumnGridBreakpoint | TColumnFlexBreakpoint> | number | string | boolean
      >,
      default: undefined
    },
    /**
     * >1584px
     */
    xl: {
      type: [Object, Number, String, Boolean] as PropType<
        TColumnBreakpoint<TColumnGridBreakpoint | TColumnFlexBreakpoint> | number | string | boolean
      >,
      default: undefined
    },
    /**
     * Only CSS Grid supported. Постоянный диапазон колонок, start или end значения, которые не изменятся.
     */
    span: {
      type: [Object, Number, String] as PropType<TColumnSpanType | number | string>,
      default: undefined
    }
  },
  'columns'
);

export const useBreakpoints = (props: IColumnProps): IUseColumn => {
  return { breakpoints: breakpointNames.map((name) => (props as any)[name]) };
};

/**
 * Build the appropriate grid className for the given set of breakpoints.
 * @param {Array<boolean|number|TColumnGridBreakpoint>} breakpoints
 * @param prefix
 * @returns {string}
 */
export const getClassNameForGridBreakpoints = (
  breakpoints: TColumnBreakpoint<TColumnGridBreakpoint>[],
  prefix: string
): string => {
  const classNames = [];

  for (let i = 0; i < breakpoints.length; i++) {
    const breakpoint = breakpoints[i];
    if (breakpoint === undefined || breakpoint === null) {
      continue;
    }

    const name = breakpointNames[i];

    // If our breakpoint is a boolean, the user has specified that the column
    // should be "auto" at this size
    if (breakpoint === true) {
      classNames.push(`${prefix}--${name}:col-span-auto`);
      continue;
    }

    // If our breakpoint is a number, the user has specified the number of
    // columns they'd like this column to span
    if (typeof breakpoint === 'number') {
      classNames.push(`${prefix}--${name}:col-span-${breakpoint}`);
      continue;
    } else if (typeof breakpoint === 'string' && breakpoint.endsWith('%')) {
      classNames.push(`${prefix}--${name}:col-span-${breakpoint.slice(0, -1)}`);
      continue;
    } else if (typeof breakpoint === 'string') {
      classNames.push(`${prefix}--${name}:col-span-${breakpoint}`);
      continue;
    }

    if (typeof breakpoint === 'object') {
      const { span, offset, start, end } = breakpoint;

      if (offset && +offset > 0) {
        classNames.push(`${prefix}--${name}:col-start-${+offset + 1}`);
      }

      if (start) {
        classNames.push(`${prefix}--${name}:col-start-${start}`);
      }

      if (end) {
        classNames.push(`${prefix}--${name}:col-end-${end}`);
      }

      if (typeof span === 'number') {
        classNames.push(`${prefix}--${name}:col-span-${span}`);
      } else if (span && span.endsWith('%')) {
        classNames.push(`${prefix}--${name}:col-span-${span.slice(0, -1)}`);
      }
    }
  }

  return classNames.join(' ');
};

/**
 * Build the appropriate flex className for the given set of breakpoints.
 * @param {Array<boolean|number|TColumnFlexBreakpoint>} breakpoints
 * @param prefix
 * @returns {string}
 */
export const getClassNameForFlexBreakpoints = (
  breakpoints: TColumnBreakpoint<TColumnFlexBreakpoint>[],
  prefix: string
): string => {
  const classNames = [];

  for (let i = 0; i < breakpoints.length; i++) {
    const breakpoint = breakpoints[i];
    if (breakpoint === undefined || breakpoint === null) {
      continue;
    }

    const name = breakpointNames[i];

    // If our breakpoint is a boolean, the user has specified that the column
    // should be "auto" at this size
    if (breakpoint === true || (typeof breakpoint === 'string' && breakpoint === '')) {
      classNames.push(`${prefix}-col-${name}`);
      continue;
    }

    // If our breakpoint is a number, the user has specified the number of
    // columns they'd like this column to span
    if (typeof breakpoint === 'number' || typeof breakpoint === 'string') {
      classNames.push(`${prefix}-col-${name}-${breakpoint}`);
      continue;
    }

    if (typeof breakpoint === 'object') {
      const { span, offset, order } = breakpoint;

      if (typeof span === 'number' || typeof span === 'string') {
        classNames.push(`${prefix}-col-${name}-${span}`);
      }

      if (span === true || !span) {
        classNames.push(`${prefix}-col-${name}`);
      }

      if (offset) {
        classNames.push(`${prefix}-offset-${name}-${offset}`);
      }

      if (order) {
        classNames.push(`${prefix}-order-${name}-${order}`);
      }
    }
  }

  return classNames.join(' ');
};

/**
 * Build the appropriate className for a span value
 */
export const getClassNameForSpan = (value: TColumnSpan<TColumnSpanType>, prefix: string) => {
  const classNames = [];

  if (typeof value === 'number' || typeof value === 'string') {
    classNames.push(`${prefix}--col-span-${value}`);
  } else if (typeof value === 'object') {
    const { span, start, end } = value;

    if (span !== undefined && span !== null) {
      classNames.push(`${prefix}--col-span-${span}`);
    }

    if (start !== undefined && start !== null) {
      classNames.push(`${prefix}--col-start-${start}`);
    }

    if (end !== undefined && end !== null) {
      classNames.push(`${prefix}--col-end-${end}`);
    }
  }

  return classNames.join(' ');
};
